/**
 * ==========================================================================
 * Datei:        video-with-text.js
 * Pfad:         assets/section-video-with-text.js
 * Zugehörig:    sections/section-video-with-text.liquid
 *               assets/section-video-with-text.css
 * Beschreibung: Custom Element <video-with-text>
 *               • Autoplay erst, wenn das Video sichtbar ist
 *               • Pausiert außerhalb des Sichtbereichs (optional)
 *               • „Bewegung reduzieren“ als Merchant-Einstellung (optional)
 *               • Eigene Play/Pause- und Ton-Buttons, Klick aufs Video
 *               • Separates Mobil-Video inkl. Wechsel beim Breakpoint
 *               • YouTube/Vimeo: Laden per Klick (DSGVO) oder bei
 *                 Sichtbarkeit, Pausieren/Fortsetzen per postMessage
 *               Keine Abhängigkeiten (kein Swiper/FoxTheme nötig).
 * ==========================================================================
 */

if (!customElements.get('video-with-text')) {
  const VISIBLE_RATIO = 0.25;
  const MEDIA_EVENTS = ['play', 'pause', 'volumechange', 'ended'];
  const ORIGINS = {
    youtube: 'https://www.youtube-nocookie.com',
    vimeo: 'https://player.vimeo.com',
  };

  class VideoWithText extends HTMLElement {
    constructor() {
      super();
      this.onIntersect = this.onIntersect.bind(this);
      this.onBreakpointChange = this.onBreakpointChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onMediaEvent = this.onMediaEvent.bind(this);
    }

    connectedCallback() {
      this.videoType = this.dataset.videoType || 'none';
      if (this.videoType === 'none') return;

      this.config = {
        autoplay: this.dataset.autoplay === 'true',
        startMuted: this.dataset.startMuted === 'true',
        pauseOffscreen: this.dataset.pauseOffscreen === 'true',
        respectReducedMotion: this.dataset.respectReducedMotion === 'true',
        clickToggle: this.dataset.clickToggle === 'true',
        consent: this.dataset.consent === 'true',
      };

      this.desktopQuery = window.matchMedia('(min-width: 768px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.frame = this.querySelector('[data-video-frame]');
      this.target = this.querySelector('[data-video-target]') || this.frame;

      this.isInView = false;
      this.userPaused = false;
      this.pausedByObserver = false;
      this.startedByUser = false;
      this.iframe = null;

      this.addEventListener('click', this.onClick);

      if (this.videoType === 'native') {
        this.videos = Array.from(this.querySelectorAll('video[data-device]'));
        this.videos.forEach((video) => {
          video.muted = this.config.startMuted;
          MEDIA_EVENTS.forEach((type) => video.addEventListener(type, this.onMediaEvent));
        });
        this.desktopQuery.addEventListener('change', this.onBreakpointChange);

        const active = this.activeVideo;
        if (active) active.preload = 'metadata';

        // Ohne Autoplay sofort den echten Zustand anzeigen (Play-Button sichtbar)
        if (!this.shouldAutoplay) this.syncState();
      }

      if ('IntersectionObserver' in window && this.frame) {
        this.observer = new IntersectionObserver(this.onIntersect, {
          threshold: [0, VISIBLE_RATIO],
        });
        this.observer.observe(this.frame);
      } else {
        this.isInView = true;
        this.onEnterView();
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.desktopQuery) this.desktopQuery.removeEventListener('change', this.onBreakpointChange);
      this.removeEventListener('click', this.onClick);

      if (this.videos) {
        this.videos.forEach((video) => {
          MEDIA_EVENTS.forEach((type) => video.removeEventListener(type, this.onMediaEvent));
          video.pause();
        });
      }
    }

    /* ------------------------------------------------------------------
     * Getter
     * ------------------------------------------------------------------ */

    get shouldAutoplay() {
      if (!this.config.autoplay) return false;
      if (this.config.respectReducedMotion && this.reducedMotionQuery.matches) return false;
      return true;
    }

    get activeVideo() {
      if (!this.videos || !this.videos.length) return null;
      if (this.videos.length === 1) return this.videos[0];
      const device = this.desktopQuery.matches ? 'desktop' : 'mobile';
      return this.videos.find((video) => video.dataset.device === device) || this.videos[0];
    }

    /* ------------------------------------------------------------------
     * Sichtbarkeit
     * ------------------------------------------------------------------ */

    onIntersect(entries) {
      entries.forEach((entry) => {
        const visible = entry.isIntersecting && entry.intersectionRatio >= VISIBLE_RATIO - 0.01;
        if (visible === this.isInView) return;
        this.isInView = visible;
        if (visible) {
          this.onEnterView();
        } else {
          this.onLeaveView();
        }
      });
    }

    onEnterView() {
      if (this.videoType === 'native') {
        if ((this.shouldAutoplay && !this.userPaused) || this.pausedByObserver) {
          this.play();
        }
      } else if (!this.iframe) {
        if (!this.config.consent) this.loadExternal({ autoplay: this.shouldAutoplay });
      } else if (this.pausedByObserver && (this.shouldAutoplay || this.startedByUser)) {
        this.sendExternalCommand('play');
      }
      this.pausedByObserver = false;
    }

    onLeaveView() {
      if (!this.config.pauseOffscreen) return;

      if (this.videoType === 'native') {
        const video = this.activeVideo;
        if (video && !video.paused) {
          this.pausedByObserver = true;
          this.pause(video, { silent: true });
        }
      } else if (this.iframe) {
        this.pausedByObserver = true;
        this.sendExternalCommand('pause');
      }
    }

    /* ------------------------------------------------------------------
     * Shopify-Video
     * ------------------------------------------------------------------ */

    play(video = this.activeVideo) {
      if (!video) return;
      const promise = video.play();
      if (promise && typeof promise.catch === 'function') {
        // Autoplay blockiert (z. B. mit Ton oder im Energiesparmodus) → Play-Button zeigen
        promise.catch(() => this.syncState());
      }
    }

    pause(video = this.activeVideo, { silent = false } = {}) {
      if (!video || video.paused) return;
      // silent = vom Script pausiert, nicht von Besucher:innen
      if (silent) video.dataset.autoPause = 'true';
      video.pause();
    }

    togglePlay() {
      const video = this.activeVideo;
      if (!video) return;
      if (video.paused) {
        this.userPaused = false;
        this.play(video);
      } else {
        this.pause(video);
      }
    }

    toggleMute() {
      const video = this.activeVideo;
      if (!video) return;
      const muted = !video.muted;
      this.videos.forEach((item) => {
        item.muted = muted;
      });
      this.syncState();
    }

    onMediaEvent(event) {
      const video = event.currentTarget;
      const isActive = video === this.activeVideo;

      if (event.type === 'pause') {
        if (video.dataset.autoPause === 'true') {
          delete video.dataset.autoPause;
        } else if (isActive) {
          this.userPaused = true;
        }
      }

      if (event.type === 'play' && isActive) {
        this.userPaused = false;
        this.pausedByObserver = false;
      }

      if (isActive) this.syncState();
    }

    onBreakpointChange() {
      if (!this.videos || this.videos.length < 2) return;

      const active = this.activeVideo;
      let resume = false;

      this.videos.forEach((video) => {
        if (video !== active && !video.paused) {
          resume = true;
          this.pause(video, { silent: true });
        }
      });

      active.preload = 'metadata';

      if (this.isInView && (resume || (this.shouldAutoplay && !this.userPaused))) {
        this.play(active);
      } else {
        this.syncState();
      }
    }

    syncState() {
      const video = this.activeVideo;
      if (!video) return;

      const paused = video.paused;
      const muted = video.muted;

      this.dataset.state = paused ? 'paused' : 'playing';
      this.dataset.muted = String(muted);

      this.querySelectorAll('[data-video-action="toggle-play"]').forEach((button) => {
        const label = paused ? button.dataset.labelPlay : button.dataset.labelPause;
        if (label) button.setAttribute('aria-label', label);
      });

      this.querySelectorAll('[data-video-action="toggle-mute"]').forEach((button) => {
        const label = muted ? button.dataset.labelUnmute : button.dataset.labelMute;
        if (label) button.setAttribute('aria-label', label);
      });
    }

    /* ------------------------------------------------------------------
     * YouTube / Vimeo
     * ------------------------------------------------------------------ */

    loadExternal({ autoplay = false, fromUser = false } = {}) {
      if (this.iframe || !this.frame) return;

      const baseSrc = this.frame.dataset.embedSrc;
      if (!baseSrc) return;

      let url;
      try {
        url = new URL(baseSrc);
      } catch (error) {
        return;
      }

      url.searchParams.set('autoplay', autoplay ? '1' : '0');
      if (this.videoType === 'youtube') url.searchParams.set('origin', window.location.origin);

      const iframe = document.createElement('iframe');
      iframe.className = 'video-text__iframe';
      iframe.src = url.toString();
      iframe.title = this.frame.dataset.embedTitle || 'Video';
      iframe.allow = 'autoplay; fullscreen; picture-in-picture; encrypted-media';
      iframe.allowFullscreen = true;
      // YouTube benötigt den Referrer, sonst Fehler 153
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';

      const consent = this.querySelector('[data-video-consent]');
      if (consent) consent.remove();

      this.target.appendChild(iframe);
      this.iframe = iframe;
      this.startedByUser = fromUser;

      // Fokus nicht verlieren, wenn der Button per Tastatur ausgelöst wurde
      if (fromUser) iframe.focus();
    }

    sendExternalCommand(command) {
      const target = this.iframe && this.iframe.contentWindow;
      if (!target) return;

      if (this.videoType === 'youtube') {
        const func = command === 'play' ? 'playVideo' : 'pauseVideo';
        target.postMessage(JSON.stringify({ event: 'command', func, args: [] }), ORIGINS.youtube);
      } else if (this.videoType === 'vimeo') {
        target.postMessage(JSON.stringify({ method: command }), ORIGINS.vimeo);
      }
    }

    /* ------------------------------------------------------------------
     * Klicks (Event-Delegation)
     * ------------------------------------------------------------------ */

    onClick(event) {
      const trigger = event.target.closest('[data-video-action]');

      if (trigger && this.contains(trigger)) {
        event.preventDefault();
        switch (trigger.dataset.videoAction) {
          case 'play':
            this.userPaused = false;
            this.play();
            break;
          case 'toggle-play':
            this.togglePlay();
            break;
          case 'toggle-mute':
            this.toggleMute();
            break;
          case 'load-external':
            this.loadExternal({ autoplay: true, fromUser: true });
            break;
          default:
            break;
        }
        return;
      }

      if (
        this.config.clickToggle &&
        event.target instanceof HTMLVideoElement &&
        !event.target.controls
      ) {
        this.togglePlay();
      }
    }
  }

  customElements.define('video-with-text', VideoWithText);
}